import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, Divider, Stack, TextField, Typography } from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { exerciseRunsApi, type ExerciseRun } from '@/api/exercises'
import { getErrorMessage } from '@/utils/error'
import type { DictationContent } from '../contentTypes'
import type { RendererProps } from './GapRenderers'

/** Счётчик прослушиваний: `left === null` — лимита нет */
type PlaysState = { used: number; left: number | null; max: number | null }

/** Статусы пословного разбора — те же три, что считает `DictationChecker` */
type DiffStatus = 'match' | 'missing' | 'extra'

/**
 * Строка пословного разбора.
 *
 * У совпадения есть оба слова, у пропущенного — только эталонное, у лишнего —
 * только набранное учеником: сервер кладёт в строку ровно то, что в ней есть.
 */
type DiffRow = { status: DiffStatus; expected?: string; actual?: string }

/** Число или null: с сервера может прийти и строка, и вовсе ничего */
function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * Счётчик из ресурса попытки. `max_plays` читаем защитно: в типе ответа поля
 * ещё нет, а лимит задания подойдёт как запасной — попытка решается по тому же
 * контенту.
 */
function playsOf(run: ExerciseRun, fallbackMax: number | null): PlaysState {
  const max = num((run as ExerciseRun & { max_plays?: unknown }).max_plays) ?? fallbackMax
  const used = num(run.plays_used) ?? 0
  const left = num(run.plays_left) ?? (max === null ? null : Math.max(max - used, 0))

  return { used, left, max }
}

/**
 * Пословный разбор из `check_details` попытки.
 *
 * Формат задаёт сервер (`DictationChecker`): `{"diff": [{"status": "match",
 * "expected": "...", "actual": "..."}, ...]}` — статусы `match`, `missing`,
 * `extra`. Поле приходит только вместе с эталонами (после сдачи ученику,
 * учителю всегда), поэтому читаем его защитно: в типе ответа его ещё нет, а у
 * активной попытки его нет и в самом ответе.
 */
function diffRowsOf(run: ExerciseRun | null | undefined): DiffRow[] {
  const details = (run as (ExerciseRun & { check_details?: unknown }) | null | undefined)?.check_details
  const rows = (details as { diff?: unknown } | null | undefined)?.diff

  if (!Array.isArray(rows)) return []

  return rows.flatMap((row): DiffRow[] => {
    const item = (row ?? {}) as { status?: unknown; expected?: unknown; actual?: unknown }

    // Незнакомый статус пропускаем, а не рисуем нейтральным: подсветка «ни то
    // ни сё» ученику врёт сильнее, чем отсутствие слова в разборе
    if (item.status !== 'match' && item.status !== 'missing' && item.status !== 'extra') return []

    return [
      {
        status: item.status,
        expected: typeof item.expected === 'string' ? item.expected : undefined,
        actual: typeof item.actual === 'string' ? item.actual : undefined,
      },
    ]
  })
}

/** Цвет статуса: совпало — успех, пропущено — предупреждение, лишнее — ошибка */
function diffColor(theme: Theme, status: DiffStatus): string {
  if (status === 'match') return theme.palette.success.main
  if (status === 'missing') return theme.palette.warning.main

  return theme.palette.error.main
}

/** 422 — «лимит исчерпан»: только на нём кнопку слушать имеет смысл гасить */
function isLimitError(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 422
}

/**
 * Пословный разбор проверенного диктанта.
 *
 * Ученик из одного процента не понимает, где именно не расслышал, а учитель
 * ставит балл, не видя, чем ответ отличается от эталона: разбор сервер считает
 * при проверке, и показать его — единственное, ради чего работа открывается
 * повторно.
 */
function DictationReview({ rows }: { rows: DiffRow[] }) {
  const { t } = useTranslation()

  const counts = useMemo(
    () => ({
      match: rows.filter((row) => row.status === 'match').length,
      missing: rows.filter((row) => row.status === 'missing').length,
      extra: rows.filter((row) => row.status === 'extra').length,
    }),
    [rows],
  )

  const label: Record<DiffStatus, string> = {
    match: t('exercises.run.dictationDiffMatch', 'Совпало'),
    missing: t('exercises.run.dictationDiffMissing', 'Пропущено'),
    extra: t('exercises.run.dictationDiffExtra', 'Лишнее'),
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('exercises.run.dictationReview', 'Разбор по словам')}
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Chip size="small" color="success" variant="outlined" label={`${label.match}: ${counts.match}`} />
        <Chip size="small" color="warning" variant="outlined" label={`${label.missing}: ${counts.missing}`} />
        <Chip size="small" color="error" variant="outlined" label={`${label.extra}: ${counts.extra}`} />
      </Stack>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {rows.map((row, index) => (
          <Box
            key={index}
            component="span"
            title={label[row.status]}
            sx={(theme) => ({
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              bgcolor: alpha(diffColor(theme, row.status), 0.14),
              color: diffColor(theme, row.status),
              // Лишнее слово зачёркнуто, пропущенное подчёркнуто пунктиром:
              // цвет один на всех при дальтонизме, и без формы статус не читается
              textDecoration: row.status === 'extra' ? 'line-through' : 'none',
              borderBottom: row.status === 'missing' ? `2px dotted ${diffColor(theme, row.status)}` : 'none',
            })}
          >
            {row.status === 'extra' ? (row.actual ?? row.expected ?? '') : (row.expected ?? row.actual ?? '')}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

/**
 * Диктант ученика: запись с ограничением прослушиваний и поле для набора.
 *
 * Счётчик на экране — подсказка, а не правило: лимит держит сервер. Начальное
 * значение берём у него же — локальный ноль после перезагрузки страницы обещал
 * ученику «осталось 3 из 3», когда прослушиваний уже не было. Ответ уходит
 * обычной кнопкой сдачи задания — отдельного экрана у диктанта больше нет.
 */
export function DictationRenderer({
  content,
  answers,
  onChange,
  readOnly,
  runId,
  run,
}: RendererProps<DictationContent> & { runId?: number; run?: ExerciseRun | null }) {
  const { t } = useTranslation()
  const audioRef = useRef<HTMLAudioElement>(null)

  const contentMaxPlays = content.max_plays ?? null
  // Попытка от родителя — источник правды с первого кадра: пока летел
  // собственный запрос, экран обещал «осталось 3 из 3» и держал кнопку живой
  const [plays, setPlays] = useState<PlaysState>(() =>
    run ? playsOf(run, contentMaxPlays) : { used: 0, left: contentMaxPlays, max: contentMaxPlays },
  )
  const [fetchedRun, setFetchedRun] = useState<ExerciseRun | null>(null)
  const [exhausted, setExhausted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const audioUrl = content.audio?.url ?? null
  const rate = Number(content.playback_rate ?? 1)

  useEffect(() => {
    // playbackRate живёт на элементе, а не в атрибуте: без этого замедленная
    // начитка играла бы в обычном темпе и диктант становился непроходимым
    if (audioRef.current && Number.isFinite(rate) && rate > 0) audioRef.current.playbackRate = rate
  }, [rate, audioUrl])

  useEffect(() => {
    // Родитель перезагружает попытку по realtime-событию — счётчик едет с ней
    if (run) setPlays(playsOf(run, contentMaxPlays))
  }, [run, contentMaxPlays])

  useEffect(() => {
    // Свой запрос — только фолбэк для экранов, которые попытку вниз не
    // передают (проверка работы учителем): второй GET той же работы лишний, а
    // упавший он ещё и оставлял бы счётчик врать до первого 422
    if (run || runId === undefined) return

    let cancelled = false

    void (async () => {
      try {
        const loaded = await exerciseRunsApi.get(runId)
        if (cancelled) return
        setFetchedRun(loaded)
        setPlays(playsOf(loaded, contentMaxPlays))
      } catch {
        // Счётчик — только подсказка: настоящий лимит держит сервер, и ронять
        // из-за него экран решения незачем
      }
    })()

    return () => {
      cancelled = true
    }
  }, [run, runId, contentMaxPlays])

  const playsLeft = plays.left
  const blocked = exhausted || playsLeft === 0

  // Разбор берём из той попытки, что есть: пропс родителя или своя дозагрузка
  const diffRows = useMemo(() => diffRowsOf(run ?? fetchedRun), [run, fetchedRun])
  // Эталон приходит в контенте только тому, кому открыты ответы: ученику
  // сервер вырезает его всегда, учителю отдаёт целиком
  const sourceText = typeof content.source_text === 'string' ? content.source_text.trim() : ''

  const play = async () => {
    setError(null)

    // Предпросмотр в конструкторе идёт без прогона: там считать нечего и
    // некому — но проиграть запись учителю всё равно нужно
    if (runId === undefined) {
      setPlays((prev) => ({
        ...prev,
        used: prev.used + 1,
        left: prev.left === null ? null : Math.max(prev.left - 1, 0),
      }))
      void audioRef.current?.play()

      return
    }

    setBusy(true)
    try {
      const updated = await exerciseRunsApi.play(runId)
      setPlays((prev) => playsOf(updated, prev.max))
      void audioRef.current?.play()
    } catch (err) {
      // Гасим кнопку только на 422 «лимит исчерпан». Сетевой сбой или 500
      // прослушивание не расходуют, и запирать ученика без звука из-за чужой
      // ошибки — значит не дать сдать задание вовсе
      if (isLimitError(err)) setExhausted(true)
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('exercises.run.dictationHint', 'Прослушайте запись и наберите то, что услышали')}
      </Typography>

      {audioUrl === null ? (
        <Alert severity="warning">{t('exercises.run.audioMissing')}</Alert>
      ) : (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={readOnly || busy || blocked}
            onClick={() => void play()}
          >
            {t('exercises.run.play')}
          </Button>
          {playsLeft !== null && plays.max !== null && (
            <Typography variant="body2" color="text.secondary">
              {t('exercises.run.dictationPlaysLeft', 'Осталось {{count}} из {{total}}', {
                count: playsLeft,
                total: plays.max,
              })}
            </Typography>
          )}
        </Stack>
      )}

      {audioUrl && <Box component="audio" ref={audioRef} src={audioUrl} sx={{ display: 'none' }} />}

      {error && <Alert severity="warning">{error}</Alert>}

      <TextField
        fullWidth
        multiline
        minRows={4}
        label={t('exercises.run.dictationAnswer', 'Ваш текст')}
        value={typeof answers.text === 'string' ? answers.text : ''}
        disabled={readOnly}
        onChange={(event) => onChange({ ...answers, text: event.target.value })}
      />

      {/* Разбор и эталон — только на закрытой работе: пока попытка активна,
          сервер их и не отдаёт, а показать их решающему значило бы выдать ответ */}
      {readOnly && diffRows.length > 0 && (
        <>
          <Divider />
          <DictationReview rows={diffRows} />
        </>
      )}

      {readOnly && sourceText !== '' && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('exercises.run.dictationSource', 'Эталонный текст')}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {sourceText}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}
